/**
 * Add/Edit FAQ Form
 * 
 * Comprehensive form for creating and editing FAQs.
 * Features:
 * - Two-column layout (desktop), single-column (mobile)
 * - Rich text editor for answers
 * - Media URL validation with auto-preview (YouTube, Loom, Google Drive)
 * - Unsaved changes warning
 * - Inline validation with clear error messages
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
  Box,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormHelperText,
  Switch,
  FormControlLabel,
  Paper,
  Grid,
  Divider,
  Alert,
  Skeleton,
  Card,
  CardContent,
  Chip,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  InputAdornment,
  CircularProgress,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import PublishIcon from '@mui/icons-material/Publish';
import CancelIcon from '@mui/icons-material/Cancel';
import YouTubeIcon from '@mui/icons-material/YouTube';
import LoomIcon from '@mui/icons-material/VideoLibrary';
import DocsIcon from '@mui/icons-material/Description';
import SheetsIcon from '@mui/icons-material/TableChart';
import SlidesIcon from '@mui/icons-material/Slideshow';
import DriveIcon from '@mui/icons-material/Folder';
import ValidIcon from '@mui/icons-material/CheckCircle';
import InvalidIcon from '@mui/icons-material/Error';
import BackIcon from '@mui/icons-material/ArrowBack';
import { debounce } from 'lodash';

import MainCard from '../../../components/MainCard';
import CustomButton from '../../../components/@extended/CustomButton';
import RichTextEditor from '../../../components/common/RichTextEditor';
import {
  useGetFaqCategoriesQuery,
  useGetFaqByIdQuery,
  useCreateFaqMutation,
  useUpdateFaqMutation,
} from '../../../store/slices/apiSlice';

// Media URL validation patterns
const URL_PATTERNS = {
  YOUTUBE: [
    /(?:https?:\/\/)?(?:www\.)?youtube\.com\/watch\?.*v=([a-zA-Z0-9_-]{11})/i,
    /(?:https?:\/\/)?(?:www\.)?youtu\.be\/([a-zA-Z0-9_-]{11})/i,
    /(?:https?:\/\/)?(?:www\.)?youtube\.com\/embed\/([a-zA-Z0-9_-]{11})/i,
    /(?:https?:\/\/)?(?:www\.)?youtube\.com\/shorts\/([a-zA-Z0-9_-]{11})/i,
  ],
  LOOM: [
    /(?:https?:\/\/)?(?:www\.)?loom\.com\/share\/([a-zA-Z0-9]+)/i,
    /(?:https?:\/\/)?(?:www\.)?loom\.com\/embed\/([a-zA-Z0-9]+)/i,
  ],
  GOOGLE_DRIVE: [
    /(?:https?:\/\/)?(?:www\.)?drive\.google\.com\/file\/d\/([a-zA-Z0-9_-]+)/i,
    /(?:https?:\/\/)?(?:www\.)?drive\.google\.com\/open\?id=([a-zA-Z0-9_-]+)/i,
  ],
  GOOGLE_DOCS: /(?:https?:\/\/)?(?:www\.)?docs\.google\.com\/document\/d\/([a-zA-Z0-9_-]+)/i,
  GOOGLE_SHEETS: /(?:https?:\/\/)?(?:www\.)?docs\.google\.com\/spreadsheets\/d\/([a-zA-Z0-9_-]+)/i,
  GOOGLE_SLIDES: /(?:https?:\/\/)?(?:www\.)?docs\.google\.com\/presentation\/d\/([a-zA-Z0-9_-]+)/i,
};

// Parse media URL to extract provider and ID
const parseMediaUrl = (url) => {
  if (!url || !url.trim()) {
    return { provider: 'NONE', resourceId: null, isValid: true, embedUrl: null };
  }

  // Check YouTube patterns
  for (const pattern of URL_PATTERNS.YOUTUBE) {
    const match = url.match(pattern);
    if (match && match[1]) {
      return { 
        provider: 'YOUTUBE', 
        resourceId: match[1], 
        isValid: true,
        embedUrl: `https://www.youtube.com/embed/${match[1]}`,
      };
    }
  }

  // Check Loom patterns
  for (const pattern of URL_PATTERNS.LOOM) {
    const match = url.match(pattern);
    if (match && match[1]) {
      return { 
        provider: 'LOOM', 
        resourceId: match[1], 
        isValid: true,
        embedUrl: `https://www.loom.com/embed/${match[1]}`,
      };
    }
  }

  // Check Google Docs
  const docsMatch = url.match(URL_PATTERNS.GOOGLE_DOCS);
  if (docsMatch && docsMatch[1]) {
    return {
      provider: 'GOOGLE_DOCS',
      resourceId: docsMatch[1],
      isValid: true,
      embedUrl: `https://docs.google.com/document/d/${docsMatch[1]}/preview`,
    };
  }

  // Check Google Sheets
  const sheetsMatch = url.match(URL_PATTERNS.GOOGLE_SHEETS);
  if (sheetsMatch && sheetsMatch[1]) {
    return {
      provider: 'GOOGLE_SHEETS',
      resourceId: sheetsMatch[1],
      isValid: true,
      embedUrl: `https://docs.google.com/spreadsheets/d/${sheetsMatch[1]}/preview`,
    };
  }

  // Check Google Slides
  const slidesMatch = url.match(URL_PATTERNS.GOOGLE_SLIDES);
  if (slidesMatch && slidesMatch[1]) {
    return {
      provider: 'GOOGLE_SLIDES',
      resourceId: slidesMatch[1],
      isValid: true,
      embedUrl: `https://docs.google.com/presentation/d/${slidesMatch[1]}/embed`,
    };
  }

  // Check Google Drive (generic)
  for (const pattern of URL_PATTERNS.GOOGLE_DRIVE) {
    const match = url.match(pattern);
    if (match && match[1]) {
      return {
        provider: 'GOOGLE_DRIVE',
        resourceId: match[1],
        isValid: true,
        embedUrl: `https://drive.google.com/file/d/${match[1]}/preview`,
      };
    }
  }

  // URL provided but doesn't match supported formats
  return { provider: 'NONE', resourceId: null, isValid: false, embedUrl: null };
};

// Get provider icon
const ProviderIcon = ({ provider }) => {
  switch (provider) {
    case 'YOUTUBE':
      return <YouTubeIcon sx={{ color: '#FF0000' }} />;
    case 'LOOM':
      return <LoomIcon sx={{ color: '#625DF5' }} />;
    case 'GOOGLE_DOCS':
      return <DocsIcon sx={{ color: '#4285F4' }} />;
    case 'GOOGLE_SHEETS':
      return <SheetsIcon sx={{ color: '#0F9D58' }} />;
    case 'GOOGLE_SLIDES':
      return <SlidesIcon sx={{ color: '#F4B400' }} />;
    case 'GOOGLE_DRIVE':
      return <DriveIcon sx={{ color: '#4285F4' }} />;
    default:
      return <ValidIcon sx={{ color: 'success.main' }} />;
  }
};

// Get provider display name
const getProviderName = (provider) => {
  const names = {
    YOUTUBE: 'YouTube Video',
    LOOM: 'Loom Video',
    GOOGLE_DOCS: 'Google Docs',
    GOOGLE_SHEETS: 'Google Sheets',
    GOOGLE_SLIDES: 'Google Slides',
    GOOGLE_DRIVE: 'Google Drive File',
  };
  return names[provider] || provider;
};

// Media preview component
const MediaPreview = ({ provider, embedUrl }) => {
  if (!embedUrl) return null;

  const isVideo = ['YOUTUBE', 'LOOM'].includes(provider);
  const isGoogleDoc = ['GOOGLE_DOCS', 'GOOGLE_SHEETS', 'GOOGLE_SLIDES', 'GOOGLE_DRIVE'].includes(provider);

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="caption" color="text.secondary" gutterBottom display="block">
        {isVideo ? 'Video Preview' : 'Document Preview'}
      </Typography>
      <Box
        sx={{
          position: 'relative',
          paddingBottom: isVideo ? '56.25%' : '75%', // 16:9 for video, 4:3 for docs
          height: 0,
          overflow: 'hidden',
          borderRadius: 1,
          backgroundColor: 'grey.100',
        }}
      >
        <iframe
          src={embedUrl}
          title="Media preview"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            border: 0,
          }}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </Box>
      {isGoogleDoc && (
        <Alert severity="info" sx={{ mt: 1 }} icon={false}>
          <Typography variant="caption">
            Make sure this file is set to "Anyone with the link can view" for users to access it.
          </Typography>
        </Alert>
      )}
    </Box>
  );
};

// Unsaved changes dialog
const UnsavedChangesDialog = ({ open, onDiscard, onCancel }) => (
  <Dialog open={open} maxWidth="xs" fullWidth>
    <DialogTitle>Unsaved Changes</DialogTitle>
    <DialogContent>
      <Typography>
        You have unsaved changes. Are you sure you want to leave? Your changes will be lost.
      </Typography>
    </DialogContent>
    <DialogActions>
      <Button onClick={onCancel} color="inherit">
        Keep Editing
      </Button>
      <Button onClick={onDiscard} color="error" variant="contained">
        Discard Changes
      </Button>
    </DialogActions>
  </Dialog>
);

// Helper to strip HTML tags for validation
const stripHtmlForValidation = (html) => {
  if (!html) return '';
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || '';
};

// Form validation schema
const validationSchema = Yup.object({
  question: Yup.string()
    .required('Question is required')
    .max(500, 'Question must be 500 characters or less'),
  answer: Yup.string()
    .required('Answer is required')
    .test('min-text-length', 'Answer must be at least 10 characters', (value) => {
      const plainText = stripHtmlForValidation(value);
      return plainText.length >= 10;
    }),
  categoryId: Yup.string()
    .required('Category is required'),
  videoUrl: Yup.string()
    .url('Must be a valid URL')
    .test('valid-media-url', 'URL must be from YouTube, Loom, or Google Drive/Docs/Sheets/Slides', (value) => {
      if (!value) return true;
      const { isValid } = parseMediaUrl(value);
      return isValid;
    }),
  displayOrder: Yup.number()
    .min(0, 'Display order must be 0 or greater')
    .integer('Display order must be a whole number'),
});

const FaqForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const isEditMode = Boolean(id);
  
  // State
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(null);
  const [mediaInfo, setMediaInfo] = useState({ provider: 'NONE', resourceId: null, isValid: true, embedUrl: null });
  
  // API queries
  const { data: categoriesData, isLoading: categoriesLoading } = useGetFaqCategoriesQuery();
  const { 
    data: faqData, 
    isLoading: faqLoading,
    error: faqError 
  } = useGetFaqByIdQuery(id, { skip: !isEditMode });
  
  const [createFaq, { isLoading: isCreating }] = useCreateFaqMutation();
  const [updateFaq, { isLoading: isUpdating }] = useUpdateFaqMutation();
  
  const isSubmitting = isCreating || isUpdating;
  
  // Formik setup
  const formik = useFormik({
    initialValues: {
      question: '',
      answer: '',
      categoryId: '',
      videoUrl: '',
      isPublished: false,
      displayOrder: 0,
    },
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        const payload = {
          question: values.question.trim(),
          answer: values.answer.trim(),
          categoryId: values.categoryId,
          videoUrl: values.videoUrl?.trim() || null,
          isPublished: values.isPublished,
          displayOrder: parseInt(values.displayOrder, 10) || 0,
        };

        if (isEditMode) {
          await updateFaq({ id, payload }).unwrap();
        } else {
          await createFaq(payload).unwrap();
        }
        
        navigate('/admin/faqs');
      } catch (error) {
        console.error('Failed to save FAQ:', error);
      } finally {
        setSubmitting(false);
      }
    },
  });

  // Populate form when editing
  useEffect(() => {
    if (faqData && isEditMode) {
      formik.setValues({
        question: faqData.question || '',
        answer: faqData.answer || '',
        categoryId: faqData.categoryId || '',
        videoUrl: faqData.videoUrl || '',
        isPublished: faqData.isPublished || false,
        displayOrder: faqData.displayOrder || 0,
      });
      
      // Parse existing media URL
      if (faqData.videoUrl) {
        setMediaInfo(parseMediaUrl(faqData.videoUrl));
      }
    }
  }, [faqData, isEditMode]);

  // Debounced media URL validation
  const debouncedValidateMedia = useMemo(
    () => debounce((url) => {
      setMediaInfo(parseMediaUrl(url));
    }, 500),
    []
  );

  const handleVideoUrlChange = (e) => {
    const value = e.target.value;
    formik.setFieldValue('videoUrl', value);
    debouncedValidateMedia(value);
  };

  // Navigation with unsaved changes check
  const handleBack = useCallback(() => {
    if (formik.dirty && !formik.isSubmitting) {
      setShowUnsavedDialog(true);
      setPendingNavigation(() => () => navigate('/admin/faqs'));
    } else {
      navigate('/admin/faqs');
    }
  }, [formik.dirty, formik.isSubmitting, navigate]);

  const handleDiscardChanges = () => {
    setShowUnsavedDialog(false);
    if (pendingNavigation) {
      pendingNavigation();
    }
  };

  const handleCancelDiscard = () => {
    setShowUnsavedDialog(false);
    setPendingNavigation(null);
  };

  // Save as draft (set isPublished to false and submit)
  const handleSaveAsDraft = () => {
    formik.setFieldValue('isPublished', false);
    setTimeout(() => formik.handleSubmit(), 0);
  };

  // Publish (set isPublished to true and submit)
  const handlePublish = () => {
    formik.setFieldValue('isPublished', true);
    setTimeout(() => formik.handleSubmit(), 0);
  };

  const categories = categoriesData || [];
  const activeCategories = categories.filter((cat) => cat.isActive);
  
  // Check if form is valid for publishing
  const canPublish = formik.values.question && 
                     formik.values.answer && 
                     formik.values.categoryId &&
                     Object.keys(formik.errors).length === 0;

  // Loading state
  if (isEditMode && faqLoading) {
    return (
      <MainCard title={<Skeleton width={200} />}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Skeleton variant="rectangular" height={56} sx={{ mb: 3 }} />
            <Skeleton variant="rectangular" height={200} />
          </Grid>
          <Grid item xs={12} md={4}>
            <Skeleton variant="rectangular" height={56} sx={{ mb: 3 }} />
            <Skeleton variant="rectangular" height={56} sx={{ mb: 3 }} />
            <Skeleton variant="rectangular" height={56} />
          </Grid>
        </Grid>
      </MainCard>
    );
  }

  // Error state
  if (isEditMode && faqError) {
    return (
      <MainCard title="Error">
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load FAQ. The FAQ may have been deleted or you don't have permission to view it.
        </Alert>
        <CustomButton variant="outlined" startIcon={<BackIcon />} onClick={() => navigate('/admin/faqs')}>
          Back to FAQ List
        </CustomButton>
      </MainCard>
    );
  }

  return (
    <>
      <MainCard
        title={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CustomButton
              variant="text"
              size="small"
              startIcon={<BackIcon />}
              onClick={handleBack}
              sx={{ mr: 1 }}
            >
              Back
            </CustomButton>
            <Typography variant="h5">
              {isEditMode ? 'Edit FAQ' : 'Create New FAQ'}
            </Typography>
          </Box>
        }
      >
        <form onSubmit={formik.handleSubmit}>
          <Grid container spacing={3}>
            {/* Left Column - Primary Content */}
            <Grid item xs={12} md={8}>
              <Paper variant="outlined" sx={{ p: 3 }}>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  Content
                </Typography>
                <Divider sx={{ mb: 3 }} />

                {/* Question Field */}
                <TextField
                  fullWidth
                  id="question"
                  name="question"
                  label="Question"
                  placeholder="Enter the FAQ question..."
                  value={formik.values.question}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.question && Boolean(formik.errors.question)}
                  helperText={
                    (formik.touched.question && formik.errors.question) ||
                    `${formik.values.question.length}/500 characters`
                  }
                  sx={{ mb: 3 }}
                  required
                  inputProps={{ maxLength: 500 }}
                />

                {/* Answer Field - Rich Text Editor */}
                <RichTextEditor
                  id="answer"
                  name="answer"
                  label="Answer"
                  placeholder="Enter the answer with formatting..."
                  value={formik.values.answer}
                  onChange={(content) => formik.setFieldValue('answer', content)}
                  onBlur={() => formik.setFieldTouched('answer', true)}
                  error={formik.touched.answer && Boolean(formik.errors.answer)}
                  helperText={formik.touched.answer && formik.errors.answer}
                  required
                  minHeight={250}
                  toolbar="default"
                />
              </Paper>
            </Grid>

            {/* Right Column - Meta Settings */}
            <Grid item xs={12} md={4}>
              {/* Category & Settings */}
              <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  Settings
                </Typography>
                <Divider sx={{ mb: 3 }} />

                {/* Category Selector */}
                <FormControl 
                  fullWidth 
                  sx={{ mb: 3 }}
                  error={formik.touched.categoryId && Boolean(formik.errors.categoryId)}
                  required
                >
                  <InputLabel>Category</InputLabel>
                  <Select
                    id="categoryId"
                    name="categoryId"
                    value={formik.values.categoryId}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    label="Category"
                  >
                    {activeCategories.map((cat) => (
                      <MenuItem key={cat.id} value={cat.id}>
                        {cat.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {formik.touched.categoryId && formik.errors.categoryId && (
                    <FormHelperText>{formik.errors.categoryId}</FormHelperText>
                  )}
                </FormControl>

                {/* Display Order */}
                <TextField
                  fullWidth
                  type="number"
                  id="displayOrder"
                  name="displayOrder"
                  label="Display Order"
                  value={formik.values.displayOrder}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.displayOrder && Boolean(formik.errors.displayOrder)}
                  helperText={
                    (formik.touched.displayOrder && formik.errors.displayOrder) ||
                    'Lower numbers appear first'
                  }
                  inputProps={{ min: 0 }}
                  sx={{ mb: 3 }}
                />

                {/* Publish Toggle */}
                <FormControlLabel
                  control={
                    <Switch
                      id="isPublished"
                      name="isPublished"
                      checked={formik.values.isPublished}
                      onChange={formik.handleChange}
                      color="success"
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2" fontWeight={500}>
                        {formik.values.isPublished ? 'Published' : 'Draft'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formik.values.isPublished 
                          ? 'Visible to end users' 
                          : 'Only visible to admins'}
                      </Typography>
                    </Box>
                  }
                />
              </Paper>

              {/* Media Settings */}
              <Paper variant="outlined" sx={{ p: 3 }}>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  Media (Optional)
                </Typography>
                <Divider sx={{ mb: 3 }} />

                <TextField
                  fullWidth
                  id="videoUrl"
                  name="videoUrl"
                  label="Media URL"
                  placeholder="Paste YouTube, Loom, or Google Drive link..."
                  value={formik.values.videoUrl}
                  onChange={handleVideoUrlChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.videoUrl && Boolean(formik.errors.videoUrl)}
                  helperText={formik.touched.videoUrl && formik.errors.videoUrl}
                  InputProps={{
                    endAdornment: formik.values.videoUrl && (
                      <InputAdornment position="end">
                        {mediaInfo.isValid ? (
                          mediaInfo.provider !== 'NONE' ? (
                            <ProviderIcon provider={mediaInfo.provider} />
                          ) : (
                            <ValidIcon sx={{ color: 'success.main' }} />
                          )
                        ) : (
                          <InvalidIcon sx={{ color: 'error.main' }} />
                        )}
                      </InputAdornment>
                    ),
                  }}
                />
                
                {/* Provider indicator */}
                {mediaInfo.isValid && mediaInfo.provider !== 'NONE' && (
                  <Chip
                    size="small"
                    icon={<ProviderIcon provider={mediaInfo.provider} />}
                    label={getProviderName(mediaInfo.provider)}
                    sx={{ mt: 1 }}
                  />
                )}
                
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  Supported: YouTube, Loom, Google Drive, Docs, Sheets, Slides
                </Typography>

                {/* Media Preview */}
                {mediaInfo.isValid && mediaInfo.provider !== 'NONE' && (
                  <MediaPreview provider={mediaInfo.provider} embedUrl={mediaInfo.embedUrl} />
                )}
              </Paper>
            </Grid>
          </Grid>

          {/* Action Buttons */}
          <Box 
            sx={{ 
              mt: 3, 
              pt: 3, 
              borderTop: 1, 
              borderColor: 'divider',
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: 2,
              justifyContent: 'flex-end',
            }}
          >
            <CustomButton
              variant="outlined"
              onClick={handleBack}
              startIcon={<CancelIcon />}
              disabled={isSubmitting}
            >
              Cancel
            </CustomButton>
            
            <CustomButton
              variant="outlined"
              onClick={handleSaveAsDraft}
              startIcon={isSubmitting ? <CircularProgress size={16} /> : <SaveIcon />}
              disabled={isSubmitting || !formik.values.question || !formik.values.answer || !formik.values.categoryId}
            >
              Save as Draft
            </CustomButton>
            
            <CustomButton
              variant="contained"
              onClick={handlePublish}
              startIcon={isSubmitting ? <CircularProgress size={16} /> : <PublishIcon />}
              disabled={isSubmitting || !canPublish}
              color="success"
            >
              {isEditMode ? 'Update & Publish' : 'Publish FAQ'}
            </CustomButton>
          </Box>
        </form>
      </MainCard>

      {/* Unsaved Changes Dialog */}
      <UnsavedChangesDialog
        open={showUnsavedDialog}
        onDiscard={handleDiscardChanges}
        onCancel={handleCancelDiscard}
      />
    </>
  );
};

export default FaqForm;
